import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { Invoice } from '../types/myaccount';
import api from '../services/api';
import { LoadingBar, TableSkeleton, ToolbarSkeleton } from './MyAccountLoadingStates';
import DataTable, { type DataTableColumn } from './DataTable';
import InvoicePaymentReviewModal from './InvoicePaymentReviewModal';
import MyAccountCard from './MyAccountCard';
import MyAccountPageShell from './MyAccountPageShell';
import InvoicePaymentsTab from './InvoicePaymentsTab';
import { getStatusBadgeClassName } from '../utils/statusBadges';

interface InvoiceHistoryProps {
    perPage?: number;
}

type SortField = 'invoice_number' | 'status' | 'date' | 'due_date' | 'total' | 'balance';
type SortDirection = 'asc' | 'desc';
type InvoicePageTab = 'invoices' | 'payments';

interface InvoiceHistoryRouteProps extends InvoiceHistoryProps {
    wekaPagePermissions?: Record<string, boolean>;
}

const InvoiceHistory: React.FC<InvoiceHistoryRouteProps> = ({ perPage = 25, wekaPagePermissions = {} }) => {
    const invoicePaymentEnabled = Boolean((window as any)?.wekaApiSettings?.features?.invoice_payment);
    const [searchParams, setSearchParams] = useSearchParams();
    const canPayMultipleInvoices = wekaPagePermissions.pay_multiple_invoices !== false;
    const canViewInvoicePayments = wekaPagePermissions.view_invoice_payments !== false;
    const canShowInvoicePaymentsTab = invoicePaymentEnabled && canViewInvoicePayments;
    const showTabNavigation = canShowInvoicePaymentsTab;
    const requestedPaymentsTab = searchParams.get('tab') === 'payments';
    const activeTab: InvoicePageTab = requestedPaymentsTab && canShowInvoicePaymentsTab ? 'payments' : 'invoices';

    const [invoices, setInvoices] = useState<Invoice[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [entriesPerPage, setEntriesPerPage] = useState(perPage);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedInvoiceNumbers, setSelectedInvoiceNumbers] = useState<string[]>([]);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [creatingOrder, setCreatingOrder] = useState(false);
    const [sortField, setSortField] = useState<SortField>('date');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const fetchInvoices = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await api.getInvoices();
            setInvoices(data);
        } catch (err) {
            console.error('Error fetching invoices:', err);
            setError('Failed to load invoices. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab !== 'invoices') {
            return;
        }

        fetchInvoices();
    }, [activeTab]);

    const formatDate = (dateString?: string) => {
        if (!dateString) {
            return '-';
        }

        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount);
    };

    const filteredInvoices = (invoices || []).filter((invoice) => {
        const searchLower = searchTerm.toLowerCase();
        const invoiceDate = invoice.date ? formatDate(invoice.date) : '';
        const dueDate = invoice.due_date ? formatDate(invoice.due_date) : '';

        const matchesBasicFields = (
            invoice.id.toString().includes(searchLower) ||
            invoice.invoice_number?.toLowerCase().includes(searchLower) ||
            invoice.date.toLowerCase().includes(searchLower) ||
            invoiceDate.toLowerCase().includes(searchLower) ||
            (invoice.due_date && invoice.due_date.toLowerCase().includes(searchLower)) ||
            dueDate.toLowerCase().includes(searchLower) ||
            invoice.status?.toLowerCase().includes(searchLower) ||
            invoice.total?.toString().includes(searchLower) ||
            invoice.balance?.toString().includes(searchLower) ||
            invoice.tax?.toString().includes(searchLower) ||
            invoice.discount?.toString().includes(searchLower) ||
            invoice.erp_customer_id?.toLowerCase().includes(searchLower) ||
            invoice.customer_display_name?.toLowerCase().includes(searchLower) ||
            invoice.customer_id?.toString().includes(searchLower) ||
            invoice.customer_order_no?.toLowerCase().includes(searchLower)
        );

        const matchesLineItems = invoice.line_items?.some((item) =>
            item.sku?.toLowerCase().includes(searchLower) ||
            item.title?.toLowerCase().includes(searchLower) ||
            item.quantity?.toString().includes(searchLower) ||
            item.unit_price?.toString().includes(searchLower) ||
            item.total?.toString().includes(searchLower) ||
            item.discount?.toString().includes(searchLower)
        ) || false;

        return matchesBasicFields || matchesLineItems;
    });

    const invoiceByNumber = new Map((invoices || []).map((invoice) => [invoice.invoice_number, invoice]));
    const selectedInvoices = selectedInvoiceNumbers
        .map((invoiceNumber) => invoiceByNumber.get(invoiceNumber))
        .filter((invoice): invoice is Invoice => Boolean(invoice));
    const selectedTotal = selectedInvoices.reduce((sum, invoice) => sum + parseFloat(invoice.balance), 0);
    const showCustomerIdColumn = (invoices || []).some((invoice) => invoice.is_child_record === true);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }

        setCurrentPage(1);
    };

    const getSortIcon = (field: SortField) => {
        if (sortField !== field) {
            return <i className="fas fa-sort ms-1 text-muted"></i>;
        }

        return sortDirection === 'asc'
            ? <i className="fas fa-sort-up ms-1"></i>
            : <i className="fas fa-sort-down ms-1"></i>;
    };

    const canInvoiceBePaid = (invoice: Invoice) => invoicePaymentEnabled && canPayMultipleInvoices && parseFloat(invoice.balance) > 0;

    const toggleInvoiceSelection = (invoice: Invoice) => {
        if (!canInvoiceBePaid(invoice)) {
            return;
        }

        setSelectedInvoiceNumbers((currentSelection) => {
            if (currentSelection.includes(invoice.invoice_number)) {
                return currentSelection.filter((invoiceNumber) => invoiceNumber !== invoice.invoice_number);
            }

            return [...currentSelection, invoice.invoice_number];
        });
    };

    const handleOpenReview = () => {
        if (!canPayMultipleInvoices) {
            toast.error('Paying multiple invoices is disabled for this page.');
            return;
        }

        if (selectedInvoices.length === 0) {
            toast.error('Select at least one invoice to continue.');
            return;
        }

        setReviewOpen(true);
    };

    const handleCreatePaymentOrder = async () => {
        if (!canPayMultipleInvoices) {
            toast.error('Paying multiple invoices is disabled for this page.');
            return;
        }

        if (selectedInvoices.length === 0 || creatingOrder) {
            return;
        }

        try {
            setCreatingOrder(true);
            const response = await api.createInvoicePayment(selectedInvoices.map((invoice) => invoice.invoice_number));
            window.location.href = response.order_pay_url || response.order_view_url || '/my-account/invoices?tab=payments';
        } catch (err) {
            console.error('Failed to create invoice payment order:', err);
            toast.error(err instanceof Error ? err.message : 'Failed to create invoice payment order.');
            setCreatingOrder(false);
        }
    };

    const setActiveTab = (nextTab: InvoicePageTab) => {
        const nextSearchParams = new URLSearchParams(searchParams);

        if (nextTab === 'payments') {
            nextSearchParams.set('tab', 'payments');
        } else {
            nextSearchParams.delete('tab');
        }

        setSearchParams(nextSearchParams);
    };

    const tabNavigation = showTabNavigation ? (
        <div className="d-flex flex-wrap gap-2 mb-4" role="tablist" aria-label="Invoice sections">
            <button
                type="button"
                className={`btn btn-sm ${activeTab === 'invoices' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setActiveTab('invoices')}
                aria-pressed={activeTab === 'invoices'}
            >
                Invoices
            </button>
            {canShowInvoicePaymentsTab && (
                <button
                    type="button"
                    className={`btn btn-sm ${activeTab === 'payments' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setActiveTab('payments')}
                    aria-pressed={activeTab === 'payments'}
                >
                    Invoice Payments
                </button>
            )}
        </div>
    ) : null;

    const shellProps = {
        title: 'Invoices',
        subtitle: activeTab === 'payments'
            ? 'Review invoice payment orders, resume pending payments, and view completed payment history.'
            : invoicePaymentEnabled
                ? 'Review unpaid invoices, build a payment list, and continue to order-pay.'
                : 'Review unpaid invoices and track outstanding balances.',
        actions: activeTab === 'invoices' && invoicePaymentEnabled && canPayMultipleInvoices ? (
            <div className="text-lg-end">
                <div className="small text-muted">Selected for payment</div>
                <div className="fw-semibold">{selectedInvoices.length} invoice(s) • {formatCurrency(selectedTotal)}</div>
            </div>
        ) : undefined,
    };

    if (activeTab === 'payments') {
        return (
            <MyAccountPageShell {...shellProps}>
                {tabNavigation}
                {!canViewInvoicePayments ? (
                    <div className="alert alert-info" role="status">
                        Viewing invoice payment history is disabled for this page.
                    </div>
                ) : (
                    <InvoicePaymentsTab enabled={canViewInvoicePayments} />
                )}
            </MyAccountPageShell>
        );
    }

    const columns: DataTableColumn<Invoice>[] = [
        {
            id: 'invoice_number',
            sortable: true,
            header: 'Invoice #',
            mobileLabel: 'Invoice #',
            mobileDisplay: 'title',
            sortValue: (invoice: Invoice) => invoice.invoice_number || '',
            render: (invoice: Invoice) => (
                <Link
                    to={`/invoices/${invoice.invoice_number || invoice.id}`}
                    className="weka-account-table-link text-decoration-none"
                >
                    <strong>#{invoice.invoice_number || invoice.id}</strong>
                </Link>
            ),
        },
        {
            id: 'status',
            sortable: true,
            header: 'Status',
            mobileLabel: 'Status',
            mobileDisplay: 'field' as const,
            sortValue: (invoice: Invoice) => invoice.status || '',
            render: (invoice: Invoice) => (
                <span className={getStatusBadgeClassName('invoices', invoice.status)}>
                    {invoice.status}
                </span>
            ),
        },
        ...(showCustomerIdColumn ? [{
            id: 'customer_id',
            sortable: true,
            header: 'Customer ID',
            mobileLabel: 'Customer ID',
            mobileDisplay: 'field' as const,
            sortValue: (invoice: Invoice) => invoice.erp_customer_id || invoice.customer_id?.toString() || '',
            render: (invoice: Invoice) => invoice.erp_customer_id || invoice.customer_id?.toString() || '-',
        }] : []),
        {
            id: 'date',
            sortable: true,
            header: 'Invoice Date',
            mobileLabel: 'Invoice date',
            mobileDisplay: 'field' as const,
            sortValue: (invoice: Invoice) => new Date(invoice.date).getTime(),
            render: (invoice: Invoice) => formatDate(invoice.date),
        },
        {
            id: 'due_date',
            sortable: true,
            header: 'Due Date',
            mobileLabel: 'Due date',
            mobileDisplay: 'field' as const,
            sortValue: (invoice: Invoice) => invoice.due_date ? new Date(invoice.due_date).getTime() : 0,
            render: (invoice: Invoice) => invoice.due_date ? formatDate(invoice.due_date) : '-',
        },
        {
            id: 'total',
            sortable: true,
            header: 'Amount',
            mobileLabel: 'Amount',
            mobileDisplay: 'field' as const,
            sortValue: (invoice: Invoice) => parseFloat(invoice.total),
            render: (invoice: Invoice) => formatCurrency(parseFloat(invoice.total)),
        },
        {
            id: 'balance',
            sortable: true,
            header: 'Balance',
            mobileLabel: 'Balance',
            mobileDisplay: 'field' as const,
            sortValue: (invoice: Invoice) => parseFloat(invoice.balance),
            render: (invoice: Invoice) => {
                const hasBalance = parseFloat(invoice.balance) > 0;
                return (
                    <strong className={hasBalance ? 'text-danger' : 'text-success'}>
                        {formatCurrency(parseFloat(invoice.balance))}
                    </strong>
                );
            },
        },
        ...(invoicePaymentEnabled && canPayMultipleInvoices ? [{
            id: 'actions',
            header: 'Payment',
            mobileDisplay: 'actions' as const,
            headerStyle: { width: '180px' },
            render: (invoice: Invoice) => {
                const hasBalance = parseFloat(invoice.balance) > 0;
                const isSelected = selectedInvoiceNumbers.includes(invoice.invoice_number);

                if (!hasBalance) {
                    return <span className={getStatusBadgeClassName('invoices', 'paid')}>Paid</span>;
                }

                return (
                    <button
                        type="button"
                        className={`btn btn-sm ${isSelected ? 'btn-outline-danger' : 'btn-outline-success'}`}
                        onClick={() => toggleInvoiceSelection(invoice)}
                    >
                        <i className={`fas ${isSelected ? 'fa-minus-circle' : 'fa-plus-circle'} me-2`}></i>
                        {isSelected ? 'Remove' : 'Add to List'}
                    </button>
                );
            },
        }] : []),
    ];

    if (loading && !invoices) {
        return (
            <MyAccountPageShell
                {...shellProps}
                actions={activeTab === 'invoices' && invoicePaymentEnabled && canPayMultipleInvoices ? (
                    <div className="text-lg-end">
                        <LoadingBar height="12px" width="112px" style={{ marginBottom: '6px' }} />
                        <LoadingBar height="16px" width="148px" />
                    </div>
                ) : undefined}
            >
                {tabNavigation}
                <ToolbarSkeleton />
                <TableSkeleton columns={invoicePaymentEnabled && canPayMultipleInvoices ? 7 : 6} rows={10} />
            </MyAccountPageShell>
        );
    }

    if (error) {
        return (
            <MyAccountPageShell {...shellProps}>
                {tabNavigation}
                <div className="alert alert-danger" role="alert">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {error}
                </div>
                <button onClick={fetchInvoices} className="btn btn-primary">
                    <i className="fas fa-redo me-2"></i>
                    Try Again
                </button>
            </MyAccountPageShell>
        );
    }

    return (
        <>
            <MyAccountPageShell {...shellProps}>
                {tabNavigation}
                {requestedPaymentsTab && !canViewInvoicePayments && (
                    <div className="alert alert-info" role="status">
                        Viewing invoice payment history is disabled for this page.
                    </div>
                )}
                {invoicePaymentEnabled && !canPayMultipleInvoices && (
                    <div className="alert alert-info" role="status">
                        Paying multiple invoices is disabled for this page.
                    </div>
                )}
                {invoicePaymentEnabled && canPayMultipleInvoices && (
                    <MyAccountCard
                        variant="page"
                        slug="selected-invoices"
                        className="mb-4 weka-my-account-page__section-card"
                        wrapContent={false}
                    >
                            <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
                                <div className="flex-grow-1">
                                    <div className="weka-my-account-page__section-title weka-my-account-page__section-title--inline">
                                        <i className="fas fa-receipt"></i>
                                        <h3>Selected Invoices</h3>
                                    </div>
                                    {selectedInvoices.length === 0 ? (
                                        <p className="text-muted mb-0">Add invoices from the table below to build your payment list.</p>
                                    ) : (
                                        <div className="list-group">
                                            {selectedInvoices.map((invoice) => (
                                                <div
                                                    key={invoice.invoice_number}
                                                    className="list-group-item d-flex justify-content-between align-items-start gap-3"
                                                >
                                                    <div>
                                                        <div className="fw-semibold">Invoice #{invoice.invoice_number}</div>
                                                        <div className="text-muted small">
                                                            Issued {formatDate(invoice.date)}
                                                            {invoice.due_date ? ` • Due ${formatDate(invoice.due_date)}` : ''}
                                                        </div>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className="fw-semibold">{formatCurrency(parseFloat(invoice.balance))}</div>
                                                        <button
                                                            type="button"
                                                            className="btn btn-link btn-sm text-danger p-0"
                                                            onClick={() => toggleInvoiceSelection(invoice)}
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="border rounded p-3 bg-white" style={{ minWidth: '240px' }}>
                                    <div className="text-muted small mb-1">Payment total</div>
                                    <div className="h4 mb-3">{formatCurrency(selectedTotal)}</div>
                                    <button
                                        type="button"
                                        className="btn btn-success w-100 mb-2"
                                        onClick={handleOpenReview}
                                        disabled={selectedInvoices.length === 0}
                                    >
                                        <i className="fas fa-credit-card me-2"></i>
                                        Review & Pay
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary w-100"
                                        onClick={() => setSelectedInvoiceNumbers([])}
                                        disabled={selectedInvoices.length === 0}
                                    >
                                        Clear List
                                    </button>
                                </div>
                            </div>
                    </MyAccountCard>
                )}

                {searchTerm && (
                    <div className="mb-3 text-muted small">
                        <i className="fas fa-filter me-1"></i>
                        Showing {filteredInvoices.length} of {invoices?.length || 0} invoices
                        {filteredInvoices.length > 0 && filteredInvoices.length !== invoices?.length && (
                            <button
                                className="btn btn-link btn-sm p-0 ms-2"
                                onClick={() => setSearchTerm('')}
                            >
                                Clear search
                            </button>
                        )}
                    </div>
                )}

                <DataTable
                    rows={filteredInvoices}
                    rowKey={(invoice) => invoice.id}
                    tableId="data_table"
                    responsiveMode="stacked-cards"
                    tableClassName="table table-hover"
                    toolbar={{
                        searchValue: searchTerm,
                        onSearchChange: (value) => {
                            setSearchTerm(value);
                            setCurrentPage(1);
                        },
                        searchPlaceholder: 'Search invoices, dates, amounts, products, SKUs...',
                        entriesPerPage,
                        onEntriesPerPageChange: (value) => {
                            setEntriesPerPage(value);
                            setCurrentPage(1);
                        },
                    }}
                    emptyState={(
                        <div className="text-center py-5">
                            <i className="fas fa-file-invoice fa-3x text-muted mb-3"></i>
                            <p className="text-muted mb-0">
                                {searchTerm ? 'No invoices found matching your search.' : 'No invoices available yet.'}
                            </p>
                        </div>
                    )}
                    sortable={{
                        field: sortField,
                        direction: sortDirection,
                        clientSide: true,
                        onSort: (field) => handleSort(field as SortField),
                        getSortIcon: (field) => getSortIcon(field as SortField),
                    }}
                    pagination={{
                        currentPage,
                        itemsPerPage: entriesPerPage,
                        totalItems: filteredInvoices.length,
                        itemsLabel: 'invoices',
                        onPageChange: setCurrentPage,
                        mode: 'full',
                    }}
                    rowClassName={(invoice) => selectedInvoiceNumbers.includes(invoice.invoice_number) ? 'table-active' : undefined}
                    columns={columns}
                />
            </MyAccountPageShell>

            <InvoicePaymentReviewModal
                isOpen={reviewOpen}
                items={selectedInvoices.map((invoice) => ({
                    invoice_number: invoice.invoice_number,
                    balance: formatCurrency(parseFloat(invoice.balance)),
                    date: formatDate(invoice.date),
                    ...(invoice.due_date ? { due_date: formatDate(invoice.due_date) } : {}),
                }))}
                totalLabel={formatCurrency(selectedTotal)}
                processing={creatingOrder}
                onClose={() => {
                    if (!creatingOrder) {
                        setReviewOpen(false);
                    }
                }}
                onConfirm={handleCreatePaymentOrder}
            />
        </>
    );
};

export default InvoiceHistory;
